import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F1120pcSchFService } from './f1120pc-sch-f.service';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1120pc-sch-f',
    templateUrl: './f1120pc-sch-f.component.html',
    styleUrls: ['./f1120pc-sch-f.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120pcSchFComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;

  // Modal properties
  modalTitle = "Schedule F Losses Incurred—Section 832 (see instructions)";
  showFormLoader = true;
  crudLoading = false;

  // Tab management
  activeTab = 1;
  tabs = [
    { id: 1, name: 'Form Entry' },
    { id: 2, name: 'Form View' },
    { id: 3, name: 'Audit' },
    { id: 4, name: 'Whitepaper Info', disabled: true }
  ];

  // Entity data properties
  entityList: any[] = [];
  leId: any;
  cdr_no: any;
  leType: any;
  formName = '1120PC';
  hgt = '96%';
  systemFlag = 'D';

  // Form data
  scheduleFFormView: any = { rows: [] };
  originalData: any = {};
  formData: any = {
    "addrows": [],
    "editrows": [],
    "removerows": []
  };

  // Diagnostic/unallocated amount data
  unallocatedAmount: any = [];

  // Batch data for Audit tab
  BatchList: any[] = [];
  BatchListData: any[] = [];
  batchLoader = false;
  showBatchData = false;
  showRequestLoader = true;
  prev_index: any = null;

  // Whitepaper data
  formWhitepaper: any[] = [];
  formsAmt: number = 0;

  // Losses incurred
  lossesIncurred: number = 0;

  // Flag to prevent multiple initialization
  private isInitialized = false;

  rowData: any = {};
  filterParams: any = {};
  baseUrls: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1120pcSchFService: F1120pcSchFService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseUrls = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.isInitialized = false;
    this.cdr.markForCheck();
  }

  init() {
    try {
      console.log('init() called', {
        hasGlobalService: !!this.globalService,
        hasGENERAL_CONFIG: !!this.GENERAL_CONFIG,
        hasRowData: !!(this.rowData && Object.keys(this.rowData).length > 0),
        hasUSER_SETTINGS: !!this.USER_SETTINGS,
        isInitialized: this.isInitialized
      });

      if (!this.globalService || !this.GENERAL_CONFIG || !this.USER_SETTINGS) {
        console.log('Missing required data, skipping init');
        return;
      }

      if (this.rowData && Object.keys(this.rowData).length > 0 && !this.isInitialized) {
        console.log('Initializing F1120PC-SCH-F component');
        this.isInitialized = true;
        this.setupEntityData();
        this.fetchSchFrecords();
        this.fetchSchFimports();
      }
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
    }
  }

  private setupEntityData() {
    // CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;

    // MANDATORY ORDER: Tax Year, LEID, LE Name, LE Type, ME
    this.entityList = [
      { key: "Tax Year", value: this.rowData.TAX_YEAR },
      { key: "LEID", value: this.rowData.LEID },
      { key: "LE Name", value: this.rowData.LE_NAME },
      { key: "LE Type", value: this.rowData.LE_TYPE },
      { key: "ME", value: this.rowData.ME }
    ];

    // Initialize unallocatedAmount from rowData.dig
    this.unallocatedAmount = this.rowData.dig || [];
    
    // Ensure exception_flag is initialized to 'N' (unchecked) if not set
    if (this.unallocatedAmount && this.unallocatedAmount.length > 0) {
      this.unallocatedAmount.forEach((row: any[]) => {
        if (row && row.length > 3 && row[3]) {
          if (!row[3].exception_flag || row[3].exception_flag === null || row[3].exception_flag === undefined) {
            row[3].exception_flag = 'N';
          }
          if (!row[3].exception_msg) {
            row[3].exception_msg = '';
          }
        }
      });
    }
  }

  fetchSchFrecords() {
    const params = {
      "action_id": 603,
      "tax_year": this.rowData.TAX_YEAR,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "combination_key": this.rowData.COMBINATION_KEY
    };

    console.log('fetchSchFrecords called with params:', params);

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadPivotFormJson',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      console.log('Form data received:', data);
      this.processFormData(data);
    }, (error: any) => {
      console.error('Error loading form data:', error);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    });
  }

  fetchSchFimports() {
    const params = {
      "client_key": this.USER_SETTINGS.user.client_key,
      "process_name": this.filterParams.process_name || "1120PC Sch F",
      "status": 'Y'
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=wwledr',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.BatchList = data.jsonObject || [];
      this.BatchList.forEach((value: any, key: number) => {
        this.BatchList[key].TOGGLE = false;
      });
      this.showRequestLoader = false;
      this.cdr.detectChanges();
    }, (error: any) => {
      console.error('Error loading batch data:', error);
      this.showRequestLoader = false;
      this.BatchList = [];
      this.cdr.detectChanges();
    });
  }

  fetchSchFwhitepaper(num: string) {
    // Enable Whitepaper tab temporarily and switch to it
    this.tabs[3].disabled = false;
    this.activeTab = 4;
    this.showFormLoader = true;

    const params = {
      "action_id": 30014,
      "line_no": num,
      "tax_year": this.rowData.TAX_YEAR,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "combination_key": this.rowData.COMBINATION_KEY,
      "is_whitepaper": 1
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.showFormLoader = false;
      const form_account = data.jsonObject || [];
      this.formWhitepaper = [];
      let f_Amt = 0;
      
      form_account.forEach((value: any) => {
        this.formWhitepaper.push(value);
        f_Amt += value.AMOUNT || 0;
      });
      
      this.formsAmt = f_Amt;
      this.cdr.detectChanges();
    }, (error: any) => {
      console.error('Error loading whitepaper data:', error);
      this.showFormLoader = false;
      this.formWhitepaper = [];
      this.cdr.detectChanges();
    });
  }

  private processFormData(data: any) {
    console.log('processFormData called, data.irsForm.formItemList.length:', data.irsForm?.formItemList?.length);
    this.scheduleFFormView.rows = [];
    var saveParamDtls: any[] = [];
    var tempDataObj: any = {};
    const dataRows = data.irsForm.formItemList;

    for (var i = 1; i < dataRows.length; i++) {
      saveParamDtls = [];
      tempDataObj = {};
      saveParamDtls = dataRows[i].dataRowList[0].rowDimensions.object_id.split('~');

      for (var j = 0; j < dataRows[i].dataRowList[0].dataCellList.length; j++) {
        if (j == 0) {
          tempDataObj.num = dataRows[i].dataRowList[0].dataCellList[j].name;
        } else if (j == 1) {
          tempDataObj.description = dataRows[i].dataRowList[0].dataCellList[j].name;
        } else if (j == 2) {
          tempDataObj.lineAttrKey = dataRows[i].dataRowList[0].dataCellList[j].key.split('~')[1];
          tempDataObj.attrib_name = dataRows[i].dataRowList[0].dataCellList[j].key.split('~')[0];
          tempDataObj.attrib_order = dataRows[i].dataRowList[0].dataCellList[j].key.split('~')[2];
          tempDataObj.is_editable = dataRows[i].dataRowList[0].dataCellList[j].property.is_editable_cell == 'Y' ? false : true;
          tempDataObj.is_display = dataRows[i].dataRowList[0].dataCellList[j].property.display == 'Y' ? true : false;
          const cellValue = dataRows[i].dataRowList[0].dataCellList[j].value;
          tempDataObj.value = cellValue == null ? 0 : parseInt(cellValue);
        } else {
          // Line 13 has a second value (value2 for round-off adjustment)
          tempDataObj.lineAttrKey2 = dataRows[i].dataRowList[0].dataCellList[j].key.split('~')[1];
          tempDataObj.attrib_name2 = dataRows[i].dataRowList[0].dataCellList[j].key.split('~')[0];
          tempDataObj.attrib_order_b = dataRows[i].dataRowList[0].dataCellList[j].key.split('~')[2];
          tempDataObj.is_editable2 = dataRows[i].dataRowList[0].dataCellList[j].property.is_editable_cell == 'Y' ? false : true;
          tempDataObj.is_display2 = dataRows[i].dataRowList[0].dataCellList[j].property.display == 'Y' ? true : false;
          const cellValue2 = dataRows[i].dataRowList[0].dataCellList[j].value;
          tempDataObj.value2 = cellValue2 == null ? 0 : parseInt(cellValue2);
        }
      }
      
      tempDataObj.line_order = saveParamDtls[8];
      tempDataObj.saveParams = saveParamDtls;
      this.scheduleFFormView.rows.push(tempDataObj);
    }

    this.originalData = _.cloneDeep(this.scheduleFFormView);
    this.initFormView(this.scheduleFFormView);
    
    // CRITICAL: Turn off loader AFTER all data is processed
    this.showFormLoader = false;
    
    console.log('Form data loaded. scheduleFFormView rows:', this.scheduleFFormView.rows.length);
    console.log('showFormLoader set to FALSE:', this.showFormLoader);
    
    // Force change detection
    this.cdr.detectChanges();
  }

  loadTransactions(index: number, trans_key: string) {
    this.batchLoader = true;
    const params = {
      "client_key": this.USER_SETTINGS.user.client_key,
      "process_name": this.filterParams.process_name || "1120PC Sch F",
      "transaction_key": trans_key
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=l6gzle',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.batchLoader = false;
      this.showBatchData = true;
      this.BatchListData = data.jsonObject || [];
      this.cdr.detectChanges();
    }, (error: any) => {
      console.error('Error loading transaction data:', error);
      this.batchLoader = false;
      this.showBatchData = false;
      this.cdr.detectChanges();
    });
  }

  showUploads(index: number, trans_key: string) {
    if (this.prev_index !== null) {
      if (this.prev_index !== index) {
        this.BatchList[this.prev_index].TOGGLE = false;
        this.prev_index = index;
        this.showBatchData = false;
        this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
        this.loadTransactions(index, trans_key);
      } else {
        this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
        this.showBatchData = true;
      }
    } else {
      this.prev_index = index;
      this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
      this.loadTransactions(index, trans_key);
    }
  }

  changeTab(tabId: number) {
    // If leaving Whitepaper tab (4), disable it again
    if (this.activeTab === 4 && tabId !== 4) {
      this.tabs[3].disabled = true;
    }
    
    this.activeTab = tabId;
    this.cdr.detectChanges();
  }

  // Computation methods (matching AngularJS logic)
  computeValues() {
    const rows = this.scheduleFFormView.rows;
    
    // Helper function to convert to number safely
    const toNum = (val: any): number => {
      const num = Number(val);
      return isNaN(num) ? 0 : num;
    };
    
    // Total. Add lines 2a and 2b (row index 2 and 3 -> result in row 4)
    rows[4].value = Math.round(toNum(rows[2].value) + toNum(rows[3].value));
    
    // Add lines 1 and 2c (row 0 and 4 -> result in row 5)
    rows[5].value = Math.round(toNum(rows[0].value) + toNum(rows[4].value));
    
    // Total. Add lines 4a and 4b (row 7 and 8 -> result in row 9)
    rows[9].value = Math.round(toNum(rows[7].value) + toNum(rows[8].value));
    
    // Subtract line 4c from line 3 (row 5 - row 9 -> result in row 10)
    rows[10].value = Math.round(toNum(rows[5].value) - toNum(rows[9].value));
    
    // Losses incurred (line 5 plus line 6 less line 7) (row 10 + row 11 - row 12 -> result in row 13)
    rows[13].value = Math.round(toNum(rows[10].value) + toNum(rows[11].value) - toNum(rows[12].value));
    
    // Total. Add lines 9, 10, and 11 (row 14 + row 15 + row 16 -> result in row 17)
    rows[17].value = Math.round(toNum(rows[14].value) + toNum(rows[15].value) + toNum(rows[16].value));
    
    // Reduction of deduction under section 832(b)(5)(B). Multiply line 12 by 0.25 (row 17 * 0.25 -> result in row 18)
    rows[18].value = Math.round(toNum(rows[17].value) * 0.25);
    
    // Losses incurred deductible under section 832(c)(4). Subtract line 13 from line 8 (row 13 - (row 18 + row 18.value2) -> result in row 19)
    rows[19].value = Math.round(toNum(rows[13].value) - (toNum(rows[18].value) + toNum(rows[18].value2)));
    
    this.lossesIncurred = rows[19].value;
    
    // Update unallocatedAmount if it exists
    if (this.unallocatedAmount && this.unallocatedAmount.length >= 2) {
      this.unallocatedAmount[0][0].value = rows[18].value;
      this.unallocatedAmount[1][0].value = rows[19].value;
      
      this.unallocatedAmount[0][2].value = toNum(this.unallocatedAmount[0][0].value) - toNum(this.unallocatedAmount[0][1].value);
      this.unallocatedAmount[1][2].value = toNum(this.unallocatedAmount[1][0].value) - toNum(this.unallocatedAmount[1][1].value);
    }
    
    this.cdr.detectChanges();
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    var formRowData: any = {};
    var formRowDataB: any = {};
    var tempArray: any[] = [];

    for (var i = 0; i < this.scheduleFFormView.rows.length; i++) {
      formRowData = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: this.scheduleFFormView.rows[i].saveParams[1],
        line_order: this.scheduleFFormView.rows[i].line_order,
        attrib_order: this.scheduleFFormView.rows[i].attrib_order,
        line_description: this.scheduleFFormView.rows[i].description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: this.scheduleFFormView.rows[i].saveParams[2],
        line_no: this.scheduleFFormView.rows[i].num,
        occurence: this.scheduleFFormView.rows[i].saveParams[7],
        line_attrib_key: this.scheduleFFormView.rows[i].lineAttrKey,
        Attribute_Name: this.scheduleFFormView.rows[i].attrib_name,
        Attribute_Value: this.scheduleFFormView.rows[i].value,
        trans_dtls_key: this.scheduleFFormView.rows[i].saveParams[3],
        trans_status: this.scheduleFFormView.rows[i].saveParams[6],
        is_dirty: ''
      };
      tempArray.push(formRowData);
      
      if (typeof this.scheduleFFormView.rows[i].value2 !== "undefined") {
        formRowDataB = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: this.scheduleFFormView.rows[i].saveParams[1],
          line_order: this.scheduleFFormView.rows[i].line_order,
          attrib_order: this.scheduleFFormView.rows[i].attrib_order_b,
          line_description: this.scheduleFFormView.rows[i].description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: this.scheduleFFormView.rows[i].saveParams[2],
          line_no: this.scheduleFFormView.rows[i].num,
          occurence: this.scheduleFFormView.rows[i].saveParams[7],
          line_attrib_key: this.scheduleFFormView.rows[i].lineAttrKey2,
          Attribute_Name: this.scheduleFFormView.rows[i].attrib_name2,
          Attribute_Value: this.scheduleFFormView.rows[i].value2,
          trans_dtls_key: this.scheduleFFormView.rows[i].saveParams[3],
          trans_status: this.scheduleFFormView.rows[i].saveParams[6],
          is_dirty: ''
        };
        tempArray.push(formRowDataB);
      }
    }
    
    this.formData.editrows.push(tempArray);

    const mainData: any[] = [];
    mainData.push(this.formData);

    // Save diagnostic data
    const diagData: any[] = [];
    if (this.unallocatedAmount) {
      this.unallocatedAmount.forEach((array: any) => {
        array.forEach((obj: any, key: number) => {
          if (key === 3) {
            diagData.push(obj);
          }
        });
      });
    }

    this.f1120pcSchFService.saveF1120pcSchFForm(
      this.GENERAL_CONFIG.base_url,
      mainData,
      diagData,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this._snackBar.open('Form has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });

          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  reset() {
    this.scheduleFFormView = _.cloneDeep(this.originalData);
    this.computeValues();
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }

  initFormView(filteredFormData: any) {
    this.scheduleFFormView = filteredFormData;
    this.computeValues();
    console.log('initFormView called. Total rows:', this.scheduleFFormView.rows.length);
  }

  // TrackBy function for performance optimization
  itemTracker(index: number, item: any): any {
    return item.num || index;
  }

  // Extract text before parenthesis for line 1 description
  getDescriptionBeforeParenthesis(description: string): string {
    if (!description) return '';
    const openParenIndex = description.indexOf('(');
    if (openParenIndex === -1) return description;
    return description.substring(0, openParenIndex).trim();
  }

  // Handle value change and ensure it's a number
  onValueChange(row: any, field: string) {
    // Convert to number to avoid string concatenation
    const value = row[field];
    if (value !== null && value !== undefined && value !== '') {
      row[field] = Number(value);
    } else {
      row[field] = 0;
    }
    // Recalculate all computed values
    this.computeValues();
  }

  // Handle exception checkbox change (Y/N values like AngularJS)
  onExceptionCheckboxChange(event: any, col: any) {
    col.exception_flag = event.target.checked ? 'Y' : 'N';
    // Clear exception message if unchecked
    if (col.exception_flag === 'N') {
      col.exception_msg = '';
    }
  }
}

